import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { callApi, getApi } from '../../api/call';
import {
  normalizePhoneToThreadId,
  formatNormalizedUsPhone,
  toUsE164FromNormalized10
} from '../utils/phoneDisplay';
import '../styles/customerMessaging.css';

function formatMessageTime(ts) {
  const d = ts?.toDate ? ts.toDate() : ts ? new Date(ts) : null;
  if (!d || Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}

/** Pending sends whose body+time window is paired to a server outbound (FIFO), so duplicate text works. */
function reflectedPendingIds(pendingList, firestoreMessages) {
  const outbound = firestoreMessages
    .filter((m) => m.direction === 'outbound' && (m.createdAt?.toMillis || m.createdAt))
    .sort((a, b) => {
      const aMs = a.createdAt?.toMillis ? a.createdAt.toMillis() : new Date(a.createdAt).getTime();
      const bMs = b.createdAt?.toMillis ? b.createdAt.toMillis() : new Date(b.createdAt).getTime();
      return aMs - bMs;
    });
  const usedIdx = new Set();
  const ids = new Set();
  const sortedPending = [...pendingList].sort((a, b) => a.startedAt - b.startedAt);
  const WINDOW_BEFORE_MS = 5000;
  const WINDOW_AFTER_MS = 180000;

  for (const p of sortedPending) {
    const want = p.body.trim();
    const t0 = p.startedAt;
    const idx = outbound.findIndex((m, i) => {
      if (usedIdx.has(i)) return false;
      if (m.body.trim() !== want) return false;
      const ms = m.createdAt?.toMillis ? m.createdAt.toMillis() : new Date(m.createdAt).getTime();
      return ms >= t0 - WINDOW_BEFORE_MS && ms <= t0 + WINDOW_AFTER_MS;
    });
    if (idx >= 0) {
      usedIdx.add(idx);
      ids.add(p.id);
    }
  }
  return ids;
}

function SendGlyph() {
  return (
    <svg className="cm-send-glyph" viewBox="0 0 24 24" width="22" height="22" aria-hidden>
      <path fill="currentColor" d="M12 4l8 8h-5v8h-6v-8H4l8-8z" />
    </svg>
  );
}

/**
 * Live SMS thread + compose for one client phone (same storage as Messages inbox).
 * @param {string} [threadDocumentId] Firestore `smsThreads` doc id (use when listing inbox so legacy ids still load).
 * @param {string} [customerPhone] Raw phone or same as doc id; used with threadDocumentId to derive E.164 for send.
 * @param {string} [clientAvatarUrl] Optional profile photo URL (shown when set).
 */
export default function ClientSmsThreadPanel({
  companyId,
  customerPhone,
  threadDocumentId,
  clientDisplayName,
  clientAvatarUrl,
  variant = 'default',
  showHeader = true,
  className = ''
}) {
  const normalizedTen = useMemo(
    () => normalizePhoneToThreadId(threadDocumentId || customerPhone),
    [threadDocumentId, customerPhone]
  );

  /** Path for messages subcollection: actual doc id when provided, else canonical 10-digit id. */
  const messagesPathId = threadDocumentId || normalizedTen;

  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [composeText, setComposeText] = useState('');
  /** @type {[{ id: string, body: string, startedAt: number }]} */
  const [pendingSends, setPendingSends] = useState([]);
  const [error, setError] = useState('');
  /** Monotonic ids for optimistic pending rows (unique per send for filter/remove on failure). */
  const localPendingSeqRef = useRef(0);

  const sendStaffSmsReply = useMemo(
    () => (payload) =>
      callApi(`/api/companies/${encodeURIComponent(payload.companyId)}/sms/reply`, payload),
    []
  );

  useEffect(() => {
    if (!companyId || !messagesPathId) {
      setMessages([]);
      return undefined;
    }
    setMessagesLoading(true);
    let cancelled = false;
    const load = async () => {
      try {
        const data = await getApi(
          `/api/companies/${encodeURIComponent(companyId)}/sms/threads/${encodeURIComponent(messagesPathId)}/messages`,
          { auth: true }
        );
        if (!cancelled) {
          setMessages(data.messages || []);
          setMessagesLoading(false);
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setError('Could not load SMS history.');
          setMessagesLoading(false);
        }
      }
    };
    void load();
    const timer = setInterval(() => {
      void load();
    }, 5000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [companyId, messagesPathId]);

  useEffect(() => {
    setPendingSends([]);
  }, [companyId, messagesPathId]);

  useEffect(() => {
    setPendingSends((prev) => {
      const confirmed = reflectedPendingIds(prev, messages);
      const next = prev.filter((p) => !confirmed.has(p.id));
      return next.length === prev.length ? prev : next;
    });
  }, [messages]);

  const confirmedPendingIds = useMemo(
    () => reflectedPendingIds(pendingSends, messages),
    [pendingSends, messages]
  );

  const displayMessages = useMemo(() => {
    const synthetic = pendingSends
      .filter((p) => !confirmedPendingIds.has(p.id))
      .map((p) => ({
        id: p.id,
        body: p.body,
        direction: 'outbound',
        createdAt: { toDate: () => new Date(p.startedAt) },
        pendingOptimistic: true
      }));
    return [...messages, ...synthetic];
  }, [messages, pendingSends, confirmedPendingIds]);

  const messagesScrollRef = useRef(null);
  const lastMessageId = displayMessages.length
    ? displayMessages[displayMessages.length - 1].id
    : null;

  useLayoutEffect(() => {
    if (messagesLoading || !lastMessageId) return;
    const el = messagesScrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    const id = requestAnimationFrame(() => {
      const box = messagesScrollRef.current;
      if (box) box.scrollTop = box.scrollHeight;
    });
    return () => cancelAnimationFrame(id);
  }, [lastMessageId, messagesLoading, messagesPathId]);

  const handleSend = () => {
    const text = composeText.trim();
    if (!companyId || !normalizedTen || !text) return;
    const e164 = toUsE164FromNormalized10(normalizedTen);
    if (!e164) {
      setError('Invalid phone number.');
      return;
    }
    setError('');
    const id = `local-pending-${++localPendingSeqRef.current}`;
    const startedAt = Date.now();
    setComposeText('');
    setPendingSends((p) => [...p, { id, body: text, startedAt }]);

    sendStaffSmsReply({
      companyId,
      customerPhone: e164,
      message: text
    }).catch((e) => {
      console.error(e);
      setPendingSends((p) => p.filter((x) => x.id !== id));
      setError(e?.message || 'Failed to send SMS.');
      setComposeText((prev) => (prev ? `${text}\n${prev}` : text));
    });
  };

  const compact = variant === 'compact';
  const panelClass =
    `cm-thread-panel cm-thread-panel--phone${compact ? ' cm-thread-panel--compact' : ''}${
      className ? ` ${className}` : ''
    }`.trim();

  if (!companyId) {
    return null;
  }

  if (!messagesPathId) {
    return (
      <div className={panelClass}>
        <div className="cm-phone-bezel">
          <div className="cm-phone-screen">
            <div className="cm-phone-notch" aria-hidden />
            <p className="cm-thread-panel-empty cm-thread-panel-empty--phone">
              Add a valid 10-digit mobile number to send SMS to this client.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const displayPhone = formatNormalizedUsPhone(normalizedTen || threadDocumentId || customerPhone);

  return (
    <div className={panelClass}>
      <div className="cm-phone-bezel">
        <div className="cm-phone-screen">
          <div className="cm-phone-notch" aria-hidden />
          {showHeader ? (
            <div
              className="cm-thread-panel-header cm-thread-panel-header--phone"
              aria-label={
                [clientDisplayName, displayPhone].filter(Boolean).join(', ') || 'SMS conversation'
              }
            >
              <div className="cm-thread-panel-headerInner">
                {clientAvatarUrl ? (
                  <img
                    className="cm-thread-panel-avatar"
                    src={clientAvatarUrl}
                    alt=""
                    width={44}
                    height={44}
                    loading="lazy"
                    decoding="async"
                  />
                ) : null}
                <div className="cm-thread-panel-headerText">
                  {clientDisplayName ? (
                    <div className="cm-thread-panel-title cm-thread-panel-title--phone">{clientDisplayName}</div>
                  ) : null}
                  {displayPhone ? (
                    <div className="cm-thread-panel-sub cm-thread-panel-sub--phone">{displayPhone}</div>
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}
          {error ? <div className="cm-error cm-error--phone">{error}</div> : null}
          <div className="cm-phone-chat">
            <div
              ref={messagesScrollRef}
              className="cm-messages cm-messages--embedded cm-messages--phone"
            >
              {messagesLoading ? (
                <div className="cm-loading">Loading messages…</div>
              ) : displayMessages.length === 0 ? (
                <div className="cm-loading cm-loading--muted">No messages yet.</div>
              ) : (
                displayMessages.map((m) => {
                  const out = m.direction === 'outbound';
                  return (
                    <div key={m.id} className={`cm-bubble-row${out ? ' cm-bubble-row--out' : ''}`}>
                      <div
                        className={`cm-bubble cm-bubble--phone${out ? ' cm-bubble--out' : ' cm-bubble--in'}${
                          m.pendingOptimistic ? ' cm-bubble--pending' : ''
                        }`.trim()}
                      >
                        {m.body}
                        <div className="cm-bubble-time cm-bubble-time--phone">
                          {m.pendingOptimistic ? 'Sending…' : formatMessageTime(m.createdAt)}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            <div className="cm-compose cm-compose--embedded cm-compose--phone">
              <textarea
                value={composeText}
                onChange={(e) => setComposeText(e.target.value)}
                placeholder="Text message"
                rows={1}
                disabled={!normalizedTen}
              />
              <button
                type="button"
                className="cm-send-btn cm-send-btn--phone"
                disabled={!composeText.trim() || !normalizedTen}
                onClick={handleSend}
                aria-label="Send message"
              >
                <SendGlyph />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
